import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Search, Navigation, Zap, Clock, Star } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import axios from "axios";
import { API } from "../App";

const sampleStations = [
  {
    id: 1,
    name: "MILE PLUS Hub - College Road",
    address: "Near College of Engineering, Nashik",
    distance: "1.2 km",
    available: 4,
    total: 6,
    rating: 4.8,
    chargerTypes: ["DC Fast", "AC"],
    status: "open",
  },
  {
    id: 2,
    name: "MILE PLUS Point - Gangapur",
    address: "Gangapur Road, Near Someshwar Temple",
    distance: "2.5 km",
    available: 2,
    total: 4,
    rating: 4.6,
    chargerTypes: ["DC Fast"],
    status: "open",
  },
  {
    id: 3,
    name: "MILE PLUS Station - Panchavati",
    address: "Panchavati Main Road, Nashik",
    distance: "3.8 km",
    available: 5,
    total: 8,
    rating: 4.9,
    chargerTypes: ["DC Fast", "AC", "CCS2"],
    status: "open",
  },
  {
    id: 4,
    name: "MILE PLUS Express - Mumbai-Agra Highway",
    address: "Highway Junction, Dwarka",
    distance: "5.2 km",
    available: 6,
    total: 10,
    rating: 4.7,
    chargerTypes: ["DC Fast", "CCS2"],
    status: "open",
  },
];

const StationFinder = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStation, setSelectedStation] = useState(null);
  const [stations, setStations] = useState(sampleStations);

  useEffect(() => {
    // Fetch stations from API
    const fetchStations = async () => {
      try {
        const response = await axios.get(`${API}/stations`);
        if (response.data && response.data.length > 0) {
          setStations(response.data);
        }
      } catch (error) {
        console.log("Using sample stations");
      }
    };
    fetchStations();
  }, []);

  const filteredStations = stations.filter(
    (station) =>
      station.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      station.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <section
      id="stations"
      className="py-24 md:py-32 bg-background relative overflow-hidden"
      data-testid="station-finder-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Find Stations
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            LOCATE YOUR NEAREST
            <br />
            <span className="text-primary">CHARGING STATION</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Map Area */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative h-[500px] lg:h-[600px] rounded-2xl overflow-hidden border border-white/10 bg-background-paper">
              {/* Map Placeholder with styled background */}
              <div className="absolute inset-0 bg-background-subtle">
                {/* Grid pattern */}
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: `
                      linear-gradient(rgba(204,255,0,0.1) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(204,255,0,0.1) 1px, transparent 1px)
                    `,
                    backgroundSize: "40px 40px",
                  }}
                />

                {/* Nashik Area Label */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                  <div className="font-heading text-6xl text-white/5">NASHIK</div>
                  <p className="text-foreground-muted text-sm mt-2">
                    Interactive map coming soon
                  </p>
                </div>

                {/* Sample markers */}
                {filteredStations.slice(0, 4).map((station, index) => (
                  <motion.div
                    key={station.id}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className={`absolute cursor-pointer transition-all ${
                      selectedStation?.id === station.id ? "z-20" : "z-10"
                    }`}
                    style={{
                      top: `${20 + index * 18}%`,
                      left: `${20 + index * 15}%`,
                    }}
                    onClick={() => setSelectedStation(station)}
                  >
                    <div
                      className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        selectedStation?.id === station.id
                          ? "bg-primary scale-125"
                          : "bg-primary/80 hover:bg-primary"
                      }`}
                    >
                      <Zap className="w-5 h-5 text-black" />
                      {selectedStation?.id === station.id && (
                        <div className="absolute inset-0 rounded-full bg-primary animate-ping opacity-30" />
                      )}
                    </div>
                    {selectedStation?.id === station.id && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute top-full mt-2 left-1/2 -translate-x-1/2 glass rounded-lg p-3 w-48 text-center"
                      >
                        <p className="text-sm font-medium text-foreground truncate">
                          {station.name}
                        </p>
                        <p className="text-xs text-primary mt-1">
                          <span className="inline-block px-2 py-1 rounded-full border-2 border-[#CCFF00] text-[#CCFF00] text-xs font-medium">
                            Upcoming
                          </span>
                        </p>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Map Controls */}
              <div className="absolute top-4 right-4 flex flex-col gap-2">
                <button
                  className="w-10 h-10 glass rounded-lg flex items-center justify-center text-foreground hover:text-primary transition-colors"
                  data-testid="map-zoom-in"
                >
                  <span className="text-xl">+</span>
                </button>
                <button
                  className="w-10 h-10 glass rounded-lg flex items-center justify-center text-foreground hover:text-primary transition-colors"
                  data-testid="map-zoom-out"
                >
                  <span className="text-xl">-</span>
                </button>
                <button
                  className="w-10 h-10 glass rounded-lg flex items-center justify-center text-foreground hover:text-primary transition-colors"
                  data-testid="map-locate"
                >
                  <Navigation className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Station List */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col"
          >
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
              <Input
                type="text"
                placeholder="Search stations by name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 bg-background-paper border-white/10 focus:border-primary text-foreground placeholder:text-foreground-muted rounded-xl"
                data-testid="station-search-input"
              />
            </div>

            {/* Station Cards */}
            <div className="flex-1 space-y-4 overflow-y-auto max-h-[500px] custom-scrollbar pr-2">
              {filteredStations.map((station, index) => (
                <motion.div
                  key={station.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className={`glass-card cursor-pointer transition-all ${
                    selectedStation?.id === station.id
                      ? "border-primary"
                      : "hover:border-white/30"
                  }`}
                  onClick={() => setSelectedStation(station)}
                  data-testid={`station-card-${station.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-heading text-lg text-foreground">
                        {station.name}
                      </h3>
                      <p className="text-foreground-muted text-sm flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />
                        {station.address}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-primary">
                      <Star className="w-4 h-4 fill-primary" />
                      <span className="text-sm font-medium">{station.rating}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-primary" />
                        <span className="inline-block px-2 py-1 rounded-full border-2 border-[#CCFF00] text-[#CCFF00] text-xs font-medium">
                          Upcoming
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-secondary" />
                        <span className="text-sm text-secondary">24/7</span>
                      </div>
                    </div>
                    <span className="text-sm text-foreground-muted">
                      {station.distance}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {station.chargerTypes.map((type) => (
                      <span
                        key={type}
                        className="px-3 py-1 rounded-full bg-white/5 text-xs text-foreground-muted border border-white/10"
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Get Directions Button */}
            <Button
              className="mt-6 rounded-full bg-primary text-black font-bold hover:shadow-[0_0_20px_rgba(204,255,0,0.5)] transition-all h-14"
              disabled={!selectedStation}
              data-testid="get-directions-btn"
            >
              <Navigation className="w-5 h-5 mr-2" />
              {selectedStation
                ? `Get Directions to ${selectedStation.name.split(" - ")[1]}`
                : "Select a Station"}
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StationFinder;
